<template>
    <v-row v-if="!$store.getters.progress" style="height: max-content" class="ma-0 pb-offset">
        <v-col cols="12" class="pa-1">
            <v-card class="pa-0 background-2 round-card">
                <v-card-title class="pa-3">
                    <p class="pl-3">{{ createTitleDialog }}</p>
                </v-card-title>
                <v-divider></v-divider>

                <v-card-text>
                    <v-stepper v-model="step" vertical>

                        <!-- STEP 1: Informazioni di base -->
                        <v-stepper-step :complete="step > 1" step="1">
                            Informazioni di base
                        </v-stepper-step>
                
                        <v-stepper-content step="1">
                            <v-card outlined class="mb-5">
                                <v-card-text>
                                    <v-text-field
                                        v-model="localAutomation.name"
                                        label="Nome dell'automazione *"
                                        :rules="[v => !!v || 'Il nome è obbligatorio']"
                                        required
                                        outlined
                                        dense
                                    ></v-text-field>

                                    <!-- Selezione tipo di automazione -->
                                    <v-radio-group
                                        v-model="automationType"
                                        class="mb-4"
                                        :rules="[v => !!v || 'Il tipo di automazione è obbligatorio']"
                                    >
                                        <template v-slot:label>
                                            <div class="font-weight-medium">Tipo di automazione *</div>
                                        </template>

                                        <v-radio value="entity" class="mb-2">
                                            <template v-slot:label>
                                                <div>
                                                    <div class="font-weight-medium">Automazione su Entità</div>
                                                    <div class="text-caption grey--text">
                                                        Opera su record specifici (Lead, Company, etc.)
                                                    </div>
                                                </div>
                                            </template>
                                        </v-radio>

                                        <v-radio value="report" class="mb-2">
                                            <template v-slot:label>
                                                <div>
                                                    <div class="font-weight-medium">Automazione Report/Standalone</div>
                                                    <div class="text-caption grey--text">
                                                        Genera report o esegue azioni aggregate senza operare su entità
                                                    </div>
                                                </div>
                                            </template>
                                        </v-radio>
                                    </v-radio-group>

                                    <v-select
                                        v-if="automationType === 'entity'"
                                        v-model="localAutomation.definition.model_type"
                                        :items="availableModels"
                                        label="Tipo di entità *"
                                        :rules="[v => !!v || 'Il tipo di entità è obbligatorio']"
                                        required
                                        outlined
                                        :menu-props="{'offset-y': true}"
                                        dense
                                        :loading="loadingModelFields"
                                    ></v-select>
                                    
                                    <!-- Nuovo campo per la priorità -->
                                    <v-row align="center" class="mb-3">
                                        <v-col cols="9">
                                            <v-slider
                                                v-model="localAutomation.priority"
                                                label="Priorità di esecuzione"
                                                hint="Valore più basso = eseguito prima (0-100)"
                                                persistent-hint
                                                min="0"
                                                max="100"
                                                thumb-label
                                                hide-details="auto"
                                            ></v-slider>
                                        </v-col>
                                        <v-col cols="3">
                                            <v-text-field
                                                v-model.number="localAutomation.priority"
                                                type="number"
                                                label="Valore"
                                                min="0"
                                                max="100"
                                                outlined
                                                dense
                                                hide-details
                                            ></v-text-field>
                                        </v-col>
                                    </v-row>

                                    <v-subheader class="pl-0">Descrizione</v-subheader>
                                    <vue-editor
                                        v-model="localAutomation.description"
                                        :editorOptions="editorSettings"
                                        :editorToolbar="customToolbar"
                                        useCustomImageHandler
                                        @image-added="handleImageAdded"
                                    ></vue-editor>
                    
                                    <v-switch
                                        v-model="localAutomation.barred"
                                        label="Disattiva Automazione"
                                        color="primary"
                                        hide-details
                                        inset
                                    ></v-switch>
                                </v-card-text>
                            </v-card>
                
                            <v-btn color="primary" @click="nextStep(1)" :disabled="!isStep1Valid">
                                Continua
                                <v-icon right>mdi-arrow-right</v-icon>
                            </v-btn>
                        </v-stepper-content>
                
                        <!-- STEP 2: Definizione dei trigger -->
                        <v-stepper-step :complete="step > 2" step="2">
                            Modalità di esecuzione
                            <small>Quando si attiva l'automazione</small>
                        </v-stepper-step>
                
                        <v-stepper-content step="2">
                            <v-card outlined class="mb-5">
                                <v-card-text>
                                    <!-- Selezione modalità esecuzione -->
                                    <v-radio-group v-model="localAutomation.definition.execution_type" class="mb-4">
                                        <template v-slot:label>
                                            <div class="font-weight-medium">Modalità di esecuzione</div>
                                        </template>

                                        <v-radio v-if="automationType === 'entity'" value="event" class="mb-2">
                                            <template v-slot:label>
                                                <div>
                                                    <div class="font-weight-medium">Basata su eventi</div>
                                                    <div class="text-caption grey--text">L'automazione si attiva quando si verificano i trigger specificati</div>
                                                </div>
                                            </template>
                                        </v-radio>

                                        <v-radio value="schedule" class="mb-2">
                                            <template v-slot:label>
                                                <div>
                                                    <div class="font-weight-medium">Pianificata con CRON</div>
                                                    <div class="text-caption grey--text">L'automazione viene eseguita secondo un'espressione CRON</div>
                                                </div>
                                            </template>
                                        </v-radio>

                                        <v-radio v-if="automationType === 'report'" value="manual" class="mb-2">
                                            <template v-slot:label>
                                                <div>
                                                    <div class="font-weight-medium">Manuale</div>
                                                    <div class="text-caption grey--text">L'automazione potrà essere eseguita manualmente tramite pulsante</div>
                                                </div>
                                            </template>
                                        </v-radio>

                                        <v-radio v-if="automationType === 'entity'" value="date_field" class="mb-2">
                                            <template v-slot:label>
                                                <div>
                                                    <div class="font-weight-medium">Pianificata su campo data</div>
                                                    <div class="text-caption grey--text">L'automazione si attiva in base a un campo data dell'entità</div>
                                                </div>
                                            </template>
                                        </v-radio>
                                    </v-radio-group>
                                    
                                    <!-- Sezione per modalità pianificata con CRON -->
                                    <div v-if="localAutomation.definition.execution_type === 'schedule'" class="mb-4">
                                        <v-text-field
                                            v-model="localAutomation.definition.schedule.cron"
                                            label="Espressione CRON"
                                            hint="Esempio: '0 0 * * *' (ogni giorno a mezzanotte)"
                                            persistent-hint
                                            outlined
                                            dense
                                        ></v-text-field>
                                    </div>

                                    <!-- Sezione per modalità manuale -->
                                    <div v-if="localAutomation.definition.execution_type === 'manual'" class="mb-4">
                                        <v-alert type="info" dense text>
                                            <strong>Esecuzione Manuale</strong>
                                            <p class="mt-2 mb-0">
                                                Dopo aver salvato l'automazione, potrai eseguirla manualmente tramite il pulsante "Esegui Ora"
                                                disponibile in questa pagina o dalla lista delle automazioni.
                                            </p>
                                        </v-alert>
                                    </div>
                                    
                                    <!-- Sezione per modalità basata su campo data -->
                                    <div v-if="localAutomation.definition.execution_type === 'date_field'" class="mb-4">
                                        <v-select
                                            v-model="localAutomation.definition.schedule.date_field"
                                            :items="getDateFields()"
                                            label="Campo data"
                                            hint="Seleziona un campo data del modello"
                                            persistent-hint
                                            outlined
                                            dense
                                            :menu-props="{'offset-y': true}"
                                        ></v-select>
                                    
                                        <v-menu
                                            ref="timeMenu"
                                            v-model="timeMenu"
                                            :close-on-content-click="false"
                                            :nudge-right="40"
                                            transition="scale-transition"
                                            offset-y
                                            max-width="290px"
                                            min-width="290px"
                                            class="mt-3">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-text-field
                                                    v-model="scheduleTime"
                                                    label="Orario di esecuzione"
                                                    prepend-icon="mdi-clock-time-four-outline"
                                                    readonly
                                                    v-bind="attrs"
                                                    v-on="on"
                                                    outlined
                                                    dense
                                                    class="mt-3"
                                                ></v-text-field>
                                            </template>
                                            <v-time-picker
                                                v-if="timeMenu"
                                                v-model="scheduleTime"
                                                full-width
                                                format="24hr"
                                                @click:minute="timeMenu = false"
                                            ></v-time-picker>
                                        </v-menu>
                                    </div>
                                    
                                    <!-- Mostra la sezione dei trigger solo se è selezionata la modalità "basata su eventi" e automazione su entità -->
                                    <div v-if="localAutomation.definition.execution_type === 'event' && automationType === 'entity'">
                                        <v-text-field
                                            v-model.number="localAutomation.definition.execution_delay"
                                            type="number"
                                            min="0"
                                            label="Ritardo esecuzione (minuti)"
                                            hint="0 = esecuzione immediata. Con un ritardo, ogni nuova modifica al record azzera e riavvia il conteggio."
                                            persistent-hint
                                            outlined
                                            dense
                                            class="mb-4"
                                        ></v-text-field>

                                        <v-btn color="success" class="mb-4" @click="addTrigger">
                                            <v-icon left>mdi-plus</v-icon> Aggiungi trigger
                                        </v-btn>
                                    
                                        <v-alert v-if="!localAutomation.definition.triggers.length"
                                            type="info"
                                            text
                                            dense>
                                            Aggiungi almeno un trigger per stabilire quando l'automazione deve attivarsi
                                        </v-alert>
                                    
                                        <div v-for="(trigger, index) in localAutomation.definition.triggers" :key="index" class="mb-4">
                                            <v-card outlined>
                                                <v-card-title class="subtitle-1">
                                                    Trigger #{{ index + 1 }}
                                                    <v-spacer></v-spacer>
                                                    <v-tooltip top>
                                                        <template v-slot:activator="{ on, attrs }">
                                                            <v-btn
                                                                icon
                                                                color="error"
                                                                @click="removeTrigger(index)"
                                                                v-bind="attrs"
                                                                v-on="on">
                                                                <v-icon>mdi-delete-outline</v-icon>
                                                            </v-btn>
                                                        </template>
                                                        <span>Rimuovi Trigger</span>
                                                    </v-tooltip>
                                                </v-card-title>
                        
                                                <v-card-text>
                                                    <v-select
                                                        v-model="trigger.type"
                                                        :items="triggerTypes"
                                                        label="Tipo di trigger"
                                                        required
                                                        dense
                                                        outlined
                                                        :menu-props="{'offset-y': true}"
                                                    ></v-select>
                        
                                                    <div v-if="trigger.type === 'event'">
                                                        <v-select
                                                            v-model="trigger.event"
                                                            :items="[
                                                                { text: 'Creazione', value: 'created' },
                                                                { text: 'Aggiornamento', value: 'updated' },
                                                                { text: 'Eliminazione', value: 'deleted' }
                                                            ]"
                                                            label="Evento"
                                                            required
                                                            dense
                                                            outlined
                                                            :menu-props="{'offset-y': true}"
                                                        ></v-select>
                                                    </div>
                        
                                                    <div v-if="trigger.type === 'field_change'">
                                                        <v-autocomplete
                                                            v-model="trigger.field"
                                                            :items="getModelFields()"
                                                            label="Campo"
                                                            required
                                                            dense
                                                            outlined
                                                            :menu-props="{'offset-y': true}"
                                                            @change="onTriggerFieldChange(trigger, index)"
                                                        ></v-autocomplete>
                                                        
                                                        <!-- Valore precedente dinamico usando DynamicField -->
                                                        <dynamic-field
                                                            v-if="trigger.field"
                                                            v-model="trigger.from"
                                                            :type="getFieldType(trigger.field)"
                                                            :options="getFieldOptions(trigger.field)"
                                                            :label="'Valore precedente (opzionale)'"
                                                            :hint="'Lascia vuoto per ignorare il valore precedente'"
                                                        ></dynamic-field>
                                                        
                                                        <!-- Nuovo valore dinamico usando DynamicField -->
                                                        <dynamic-field
                                                            v-if="trigger.field"
                                                            v-model="trigger.to"
                                                            :type="getFieldType(trigger.field)"
                                                            :options="getFieldOptions(trigger.field)"
                                                            :label="'Nuovo valore (opzionale)'"
                                                            :hint="'Lascia vuoto per ignorare il nuovo valore'"
                                                        ></dynamic-field>
                                                    </div>
                                                </v-card-text>
                                            </v-card>
                                        </div>
                                    </div>
                                </v-card-text>
                            </v-card>

                            <v-btn color="primary" @click="nextStep(2)" :disabled="!isStep2Valid">
                                Continua
                                <v-icon right>mdi-arrow-right</v-icon>
                            </v-btn>

                            <v-btn text @click="step = 1" class="ml-2">
                                <v-icon left>mdi-arrow-left</v-icon>
                                Indietro
                            </v-btn>
                        </v-stepper-content>
                
                        <!-- STEP 3: Definizione delle condizioni -->
                        <v-stepper-step :complete="step > 3" step="3">
                            Condizioni
                            <small>Filtro entità che attivano l'automazione</small>
                        </v-stepper-step>
                
                        <v-stepper-content step="3">
                            <v-card outlined class="mb-5">
                                <v-card-text>
                                    <!-- Per automazioni su entità, mostra controlli completi -->
                                    <div v-if="automationType === 'entity'">
                                        <!-- Scelta modalità condizioni -->
                                        <div class="mb-4">
                                            <v-btn-toggle v-model="conditionMode" mandatory class="mb-2">
                                                <v-btn :value="'simple'" class="px-4">
                                                    <v-icon left>mdi-list-status</v-icon>
                                                    Condizioni semplici
                                                </v-btn>
                                                <v-btn :value="'advanced'" class="px-4">
                                                    <v-icon left>mdi-database</v-icon>
                                                    Query SQL avanzata
                                                </v-btn>
                                            </v-btn-toggle>
                                        </div>

                                        <!-- Pulsante aggiungi condizione (solo per modalità semplice) -->
                                        <div v-if="conditionMode === 'simple'" class="mb-4">
                                            <v-btn color="success" @click="addCondition">
                                                <v-icon left>mdi-plus</v-icon> Aggiungi condizione
                                            </v-btn>
                                        </div>
                                    </div>

                                    <!-- Per automazioni report, mostra solo titolo query avanzata -->
                                    <div v-else class="mb-4">
                                        <h3 class="subtitle-1">Condizioni Query SQL (Opzionale)</h3>
                                    </div>

                                    <v-alert v-if="automationType === 'entity' && conditionMode === 'simple' && !localAutomation.definition.conditions.items"
                                        type="info"
                                        text
                                        dense
                                        class="mb-4">
                                        Le condizioni sono opzionali. Se non ne aggiungi, l'automazione sarà applicata a tutte le entità.
                                    </v-alert>

                                    <v-alert v-if="automationType === 'report'"
                                        type="info"
                                        text
                                        dense
                                        class="mb-4">
                                        Per i report, puoi specificare una query SQL per filtrare i dati. La query è opzionale.
                                    </v-alert>
                    
                                    <!-- Condizioni semplici (solo per entità) -->
                                    <div v-if="automationType === 'entity' && conditionMode === 'simple'">
                                        <div v-for="(condition, index) in getConditionItems()" :key="index" class="mb-4">
                                            <v-card outlined>
                                                <v-card-title class="subtitle-1">
                                                    Condizione #{{ index + 1 }}
                                                    <v-spacer></v-spacer>
                                                    <v-tooltip top>
                                                        <template v-slot:activator="{ on, attrs }">
                                                            <v-btn
                                                                icon
                                                                color="error"
                                                                @click="removeCondition(index)"
                                                                v-bind="attrs"
                                                                v-on="on">
                                                                <v-icon>mdi-delete-outline</v-icon>
                                                            </v-btn>
                                                        </template>
                                                        <span>Rimuovi Condizione</span>
                                                    </v-tooltip>
                                                </v-card-title>
                        
                                                <v-card-text>
                                                    <v-autocomplete
                                                        v-model="condition.field"
                                                        :items="getModelFields()"
                                                        label="Campo"
                                                        required
                                                        dense
                                                        outlined
                                                        :menu-props="{'offset-y': true}"
                                                        @change="onFieldChange(condition, index)"
                                                    ></v-autocomplete>
                            
                                                    <v-select
                                                        v-model="condition.operator"
                                                        :items="operators"
                                                        label="Operatore"
                                                        required
                                                        dense
                                                        outlined
                                                        :menu-props="{'offset-y': true}"
                                                        @change="onOperatorChange(condition, index)"
                                                    ></v-select>
                            
                                                    <!-- Campo valore variabile in base al tipo -->
                                                    <div v-if="condition.field">
                                                        <!-- Campo booleano -->
                                                        <dynamic-field
                                                            v-if="condition.field"
                                                            v-model="condition.value"
                                                            :type="getFieldType(condition.field)"
                                                            :options="getFieldOptions(condition.field)"
                                                            :multiple="condition.operator === 'in'"
                                                            :label="'Valore'"
                                                            :required="true"
                                                            :loading="loadingFieldOptions[index]"
                                                        ></dynamic-field>
                                                    </div>
                                                </v-card-text>
                                            </v-card>
                                        </div>
                                    </div>
                    
                                    <!-- Query avanzata (per entità con conditionMode advanced, o sempre per report) -->
                                    <div v-if="(automationType === 'entity' && conditionMode === 'advanced') || automationType === 'report'">
                                        <code-editor
                                            v-model="advancedQuery"
                                            label="Query SQL personalizzata"
                                            :hint="automationType === 'entity' ? 'Utilizza {campo} come placeholder per i campi dell\'entità' : 'Utilizza placeholder globali: {current_date}, {current_user_name}, etc.'"
                                            :rows="6"
                                            :outlined="true"
                                            :dense="false"
                                            language="sql"
                                        ></code-editor>

                                        <v-alert
                                            type="warning"
                                            text
                                            dense
                                            class="mt-3">
                                            <span v-if="automationType === 'entity'">
                                                Attenzione: Utilizzare una query SQL personalizzata richiede conoscenze avanzate.
                                                La query deve restituire almeno un risultato perché l'automazione si attivi.
                                            </span>
                                            <span v-else>
                                                Per i report, la query SQL è opzionale e può essere usata per filtrare o preparare dati globali.
                                            </span>
                                        </v-alert>

                                        <v-card v-if="automationType === 'entity'" outlined class="mt-3">
                                            <v-card-subtitle class="font-weight-bold">
                                                <v-icon small class="mr-2">mdi-information-outline</v-icon>
                                                Documentazione Query SQL
                                            </v-card-subtitle>
                                            <v-card-text>
                                                <p class="mb-2"><strong>Formati supportati:</strong></p>

                                                <div class="mb-3">
                                                    <p class="mb-1 font-weight-medium">1. Clausola WHERE semplice:</p>
                                                    <code class="d-block pa-2 background-1">status = 'active' AND revenue > 100000</code>
                                                </div>

                                                <div class="mb-3">
                                                    <p class="mb-1 font-weight-medium">2. Query SELECT con ID specifici:</p>
                                                    <code class="d-block pa-2 background-1">SELECT * FROM companies WHERE id IN (1, 2, 5)</code>
                                                </div>

                                                <div class="mb-3">
                                                    <p class="mb-1 font-weight-medium">3. Query con JOIN:</p>
                                                    <code class="d-block pa-2 background-1">SELECT c.* FROM companies c<br>LEFT JOIN contacts ct ON ct.company_id = c.id<br>WHERE ct.type = 'commercial'<br>GROUP BY c.id</code>
                                                </div>

                                                <div class="mb-3">
                                                    <p class="mb-1 font-weight-medium">4. Query con aggregazioni:</p>
                                                    <code class="d-block pa-2 background-1">SELECT c.* FROM companies c<br>LEFT JOIN orders o ON o.company_id = c.id<br>GROUP BY c.id<br>HAVING SUM(o.total) > 50000</code>
                                                </div>

                                                <v-divider class="my-3"></v-divider>

                                                <p class="mb-2"><strong>Operazioni bloccate per sicurezza:</strong></p>
                                                <ul class="mb-0">
                                                    <li>UPDATE, DELETE, INSERT, DROP, CREATE, ALTER, TRUNCATE</li>
                                                    <li>Operazioni su tabelle di sistema (migrations, oauth_*, password_resets)</li>
                                                    <li>Pattern sospetti (UNION SELECT, LOAD_FILE, INTO OUTFILE, SLEEP, BENCHMARK)</li>
                                                </ul>
                                            </v-card-text>
                                        </v-card>
                                    </div>
                    
                                    <!-- Operatore logico per più condizioni -->
                                    <v-radio-group v-if="conditionMode === 'simple' && getConditionItems().length > 1"
                                        v-model="conditionsLogic"
                                        row
                                        class="mt-4">
                                        <template v-slot:label>
                                            <div>Operatore logico tra condizioni</div>
                                        </template>
                                        <v-radio value="and" label="AND (Tutte le condizioni devono essere vere)"></v-radio>
                                        <v-radio value="or" label="OR (Almeno una condizione deve essere vera)"></v-radio>
                                    </v-radio-group>
                                </v-card-text>
                            </v-card>
                
                            <v-btn color="primary" @click="nextStep(3)" :disabled="!isStep3Valid">
                                Continua
                                <v-icon right>mdi-arrow-right</v-icon>
                            </v-btn>
                
                            <v-btn text @click="step = 2" class="ml-2">
                                <v-icon left>mdi-arrow-left</v-icon>
                                Indietro
                            </v-btn>
                        </v-stepper-content>
                
                        <!-- STEP 4: Definizione delle azioni -->
                        <v-stepper-step :complete="step > 4" step="4">
                            Azioni
                            <small>Cosa succede quando l'automazione si attiva</small>
                        </v-stepper-step>
                
                        <v-stepper-content step="4">
                            <v-card outlined class="mb-5">
                                <v-card-text>
                                    <v-alert v-if="!localAutomation.definition.actions.length"
                                        type="warning"
                                        text
                                        dense>
                                        Devi aggiungere almeno un'azione per l'automazione
                                    </v-alert>
                    
                                    <div v-for="(action, index) in localAutomation.definition.actions" :key="index" class="mb-4">
                                        <v-card outlined>
                                            <v-card-title class="subtitle-1">
                                                Azione #{{ index + 1 }}
                                                <v-spacer></v-spacer>
                                                <v-tooltip top>
                                                    <template v-slot:activator="{ on, attrs }">
                                                        <v-btn
                                                            icon
                                                            color="error"
                                                            @click="removeAction(index)"
                                                            v-bind="attrs"
                                                            v-on="on">
                                                            <v-icon>mdi-delete-outline</v-icon>
                                                        </v-btn>
                                                    </template>
                                                    <span>Rimuovi Azione</span>
                                                </v-tooltip>
                                            </v-card-title>
                        
                                            <v-card-text>
                                                <v-select
                                                    v-model="action.type"
                                                    :items="filteredActionTypes"
                                                    label="Tipo di azione"
                                                    required
                                                    dense
                                                    outlined
                                                    :menu-props="{'offset-y': true}" 
                                                    @change="onActionTypeChange(action, index)"                                       
                                                ></v-select>
                            
                                                <!-- Azione: aggiorna campo -->
                                                <div v-if="action.type === 'update_fields'">
                                                    <div class="d-flex mb-2">
                                                        <v-btn color="primary" text small class="mr-2" @click="addUpdateField(action)">
                                                            <v-icon small left>mdi-plus</v-icon> Aggiungi campo
                                                        </v-btn>
                                                    </div>
                                            
                                                    <div v-if="!action.fields || action.fields.length === 0">
                                                        <v-alert type="info" text dense class="mt-2">
                                                            Aggiungi almeno un campo da aggiornare
                                                        </v-alert>
                                                    </div>
                                            
                                                    <div v-for="(field, index) in action.fields" :key="index" class="d-flex align-center mb-2">
                                                        <v-autocomplete
                                                            v-model="field.key"
                                                            :items="getModelFields()"
                                                            label="Campo"
                                                            dense
                                                            class="mr-2"
                                                            outlined
                                                            :menu-props="{'offset-y': true}"
                                                        ></v-autocomplete>
                                                        
                                                        <!-- Uso DynamicField per tutti i campi -->
                                                        <dynamic-field
                                                            v-model="field.value"
                                                            :type="getFieldTypeForInput(field.key)"
                                                            :options="getFieldOptions(field.key)"
                                                            :label="'Valore'"
                                                            :required="true"
                                                        ></dynamic-field>
                                            
                                                        <v-btn icon color="error" @click="removeUpdateField(action, index)">
                                                            <v-icon style="margin-top: -22px !important">mdi-delete-outline</v-icon>
                                                        </v-btn>
                                                    </div>
                                                </div>
                            
                                                <!-- Azione: invia email -->
                                                <div v-else-if="action.type === 'send_email'">
                                                    <!-- Tipo di corpo email -->
                                                    <v-select
                                                        v-model="action.body_type"
                                                        :items="emailBodyTypes"
                                                        @change="onBodyTypeChange(action, $event)"
                                                        label="Tipo Corpo Email"
                                                        required
                                                        dense
                                                        outlined
                                                        :menu-props="{'offset-y': true}"
                                                    ></v-select>

                                                    <!-- Se Template -->
                                                    <v-select v-if="action.body_type === 'template'"
                                                        v-model="action.template_id"
                                                        :items="emailTemplates[localAutomation.definition.model_type] || []"
                                                        label="Template email"
                                                        required
                                                        dense
                                                        outlined
                                                        :loading="loadingEmailTemplates"
                                                        :menu-props="{'offset-y': true}"
                                                    ></v-select>

                                                    <!-- Se SQL Query o SQL Query CSV -->
                                                    <div v-if="action.body_type === 'sql_query' || action.body_type === 'sql_query_csv'">
                                                        <code-editor
                                                            v-model="action.sql_query"
                                                            :label="action.body_type === 'sql_query_csv' ? 'Query SQL per generare il file CSV da allegare' : 'Query SQL per generare il corpo dell\'email'"
                                                            :required="true"
                                                            language="sql"
                                                        ></code-editor>

                                                        <v-alert v-if="action.body_type === 'sql_query'" type="info" dense text class="mt-3">
                                                            <strong>La query verrà eseguita e i risultati formattati in una tabella HTML</strong>
                                                            <ul class="mt-2">
                                                                <li>Solo query SELECT sono permesse</li>
                                                                <li>Placeholder: {id}, '{class}', '{current_user_name}' (quota le stringhe manualmente)</li>
                                                            </ul>
                                                        </v-alert>

                                                        <v-alert v-if="action.body_type === 'sql_query_csv'" type="info" dense text class="mt-3">
                                                            <strong>La query verrà eseguita e i risultati allegati come file CSV</strong>
                                                            <ul class="mt-2">
                                                                <li>Solo query SELECT sono permesse</li>
                                                                <li>Il file CSV sarà allegato all'email con nome "report_[data].csv"</li>
                                                                <li>Placeholder: {current_date}, {current_user_name} (quota le stringhe manualmente)</li>
                                                            </ul>
                                                        </v-alert>
                                                    </div>

                                                    <!-- Se più query: una tabella per query, tutte nella stessa email -->
                                                    <div v-if="action.body_type === 'sections'">
                                                        <v-alert type="info" dense text class="mb-3">
                                                            <strong>Ogni query genera una tabella separata, tutte nella stessa email</strong>
                                                            <ul class="mt-2">
                                                                <li>Solo query SELECT sono permesse</li>
                                                                <li>Se una query fallisce, le altre vengono comunque inviate</li>
                                                                <li>Placeholder: {current_date}, {current_datetime}, {current_user_name} (quota le stringhe manualmente)</li>
                                                            </ul>
                                                        </v-alert>

                                                        <v-card
                                                            v-for="(section, sIndex) in (action.sections || [])"
                                                            :key="sIndex"
                                                            outlined
                                                            class="mb-3"
                                                        >
                                                            <v-card-title class="py-2 subtitle-2 grey lighten-4">
                                                                Query {{ sIndex + 1 }}
                                                                <v-spacer></v-spacer>
                                                                <v-btn icon small :disabled="sIndex === 0" @click="moveSection(action, sIndex, -1)">
                                                                    <v-icon small>mdi-arrow-up</v-icon>
                                                                </v-btn>
                                                                <v-btn icon small :disabled="sIndex === (action.sections.length - 1)" @click="moveSection(action, sIndex, 1)">
                                                                    <v-icon small>mdi-arrow-down</v-icon>
                                                                </v-btn>
                                                                <v-btn icon small color="error" @click="removeSection(action, sIndex)">
                                                                    <v-icon small>mdi-delete-outline</v-icon>
                                                                </v-btn>
                                                            </v-card-title>

                                                            <v-card-text class="pt-3">
                                                                <v-text-field
                                                                    v-model="section.title"
                                                                    label="Titolo tabella"
                                                                    hint="Compare come intestazione della tabella nell'email"
                                                                    dense
                                                                    outlined
                                                                    hide-details="auto"
                                                                    class="mb-3"
                                                                ></v-text-field>

                                                                <v-text-field
                                                                    v-model="section.description"
                                                                    label="Sottotitolo (opzionale)"
                                                                    dense
                                                                    outlined
                                                                    hide-details="auto"
                                                                    class="mb-3"
                                                                ></v-text-field>

                                                                <code-editor
                                                                    v-model="section.sql_query"
                                                                    label="Query SQL"
                                                                    :required="true"
                                                                    language="sql"
                                                                ></code-editor>
                                                            </v-card-text>
                                                        </v-card>

                                                        <v-btn small color="primary" outlined @click="addSection(action)">
                                                            <v-icon left small>mdi-plus</v-icon>
                                                            Aggiungi query
                                                        </v-btn>
                                                    </div>

                                                    <!-- Se Custom -->
                                                    <div v-if="action.body_type === 'custom'">
                                                        <text-editor
                                                            v-model="action.body"
                                                            label="Corpo Email"
                                                            :required="true"
                                                        ></text-editor>

                                                        <v-alert type="info" dense text class="mt-2">
                                                            <strong>Placeholder disponibili (senza apici, verranno sostituiti direttamente):</strong>
                                                            <ul>
                                                                <li><code>{id}</code>, <code>{class}</code>, <code>{nome_campo}</code></li>
                                                                <li><code>{current_user_name}</code>, <code>{current_user_email}</code></li>
                                                            </ul>
                                                            <em>Nota: Nel corpo email i placeholder vengono sostituiti come testo, non serve quotarli</em>
                                                        </v-alert>
                                                    </div>

                                                    <!-- Campo oggetto -->
                                                    <v-text-field
                                                        v-model="action.subject"
                                                        label="Oggetto Email"
                                                        hint="Puoi usare placeholder come {id}, {name}, etc."
                                                        persistent-hint
                                                        required
                                                        dense
                                                        outlined
                                                    ></v-text-field>

                                                    <!-- Destinatario -->
                                                    <v-select
                                                        v-model="action.recipient_type"
                                                        :items="[
                                                            { text: 'Email dell\'entità', value: 'entity_email' },
                                                            { text: 'Email personalizzata', value: 'custom_email' }
                                                        ]"
                                                        label="Destinatario"
                                                        required
                                                        dense
                                                        outlined
                                                        :menu-props="{'offset-y': true}"
                                                    ></v-select>

                                                    <v-text-field v-if="action.recipient_type === 'entity_email'"
                                                        v-model="action.to_field"
                                                        label="Campo email"
                                                        hint="Campo dell'entità che contiene l'email (es. primary_email)"
                                                        persistent-hint
                                                        required
                                                        dense
                                                        outlined
                                                    ></v-text-field>

                                                    <v-text-field v-if="action.recipient_type === 'custom_email'"
                                                        v-model="action.to"
                                                        label="Email destinatario"
                                                        required
                                                        dense
                                                        outlined
                                                    ></v-text-field>

                                                    <!-- Campi aggiuntivi mittente -->
                                                    <v-text-field
                                                        v-model="action.from"
                                                        label="Email Mittente (lascia vuoto per default)"
                                                        dense
                                                        outlined
                                                    ></v-text-field>

                                                    <v-text-field
                                                        v-model="action.from_name"
                                                        label="Nome Mittente"
                                                        dense
                                                        outlined
                                                    ></v-text-field>

                                                    <!-- CC e CCN -->
                                                    <v-combobox
                                                        v-model="action.cc"
                                                        :items="emailSuggestions"
                                                        label="CC"
                                                        multiple
                                                        chips
                                                        small-chips
                                                        deletable-chips
                                                        dense
                                                        outlined
                                                        hint="Puoi inserire più indirizzi email"
                                                        persistent-hint
                                                    ></v-combobox>

                                                    <v-combobox
                                                        v-model="action.ccn"
                                                        :items="emailSuggestions"
                                                        label="CCN (Copia Nascosta)"
                                                        multiple
                                                        chips
                                                        small-chips
                                                        deletable-chips
                                                        dense
                                                        outlined
                                                        hint="Puoi inserire più indirizzi email"
                                                        persistent-hint
                                                    ></v-combobox>
                                                </div>
                                                
                                                <!-- Azione: aggiungi alla coda di chiamate -->
                                                <div v-else-if="action.type === 'add_to_call_queue'">
                                                    <v-select
                                                        v-model="action.callqueue_id"
                                                        :items="callQueueOptions"
                                                        label="Coda di chiamate"
                                                        required
                                                        dense
                                                        outlined
                                                        :loading="loadingCallQueueOptions"
                                                        :menu-props="{'offset-y': true}"
                                                    ></v-select>
                                                </div>
                                                
                                                <!-- Azione: esegui query SQL -->
                                                <div v-else-if="action.type === 'execute_sql'">
                                                    <code-editor
                                                        v-model="action.sql_query"
                                                        label="Query SQL"
                                                        hint="Usa {id} per l'ID del record, {class} per il tipo, {nome_campo} per campi standard, {customfield_XXX} per custom fields"
                                                        :persistent-hint="true"
                                                        :rows="10"
                                                        :outlined="true"
                                                        :dense="true"
                                                        :required="true"
                                                        language="sql"
                                                    ></code-editor>
                                                    
                                                    <v-alert type="info" dense text class="mt-3">
                                                        <strong>Placeholder disponibili:</strong>
                                                        <ul class="mt-2">
                                                            <li><code>{id}</code> - ID del record corrente (numerico, non quotato)</li>
                                                            <li><code>{class}</code> - Tipo del modello (es. 'Company', 'Lead' - usa apici singoli)</li>
                                                            <li><code>{nome_campo}</code> - Valore di un campo del modello (aggiungi apici per stringhe)</li>
                                                            <li><code>{customfield_118}</code> - Valore di un campo personalizzato (aggiungi apici per stringhe)</li>
                                                            <li><code>{current_user_id}</code> - ID dell'utente (numerico, non quotato)</li>
                                                            <li><code>{current_user_name}</code> - Nome utente (aggiungi apici: '{current_user_name}')</li>
                                                            <li><code>{current_user_email}</code> - Email utente (aggiungi apici: '{current_user_email}')</li>
                                                        </ul>
                                                        <strong>Nota importante:</strong> Le variabili stringa devono essere quotate manualmente con apici singoli.
                                                        <br>Esempio: <code>VALUES (76, {id}, '{class}', '{current_user_name}', NOW(), NOW())</code>
                                                    </v-alert>
                                                    
                                                    <v-alert type="warning" dense text class="mt-2">
                                                        <strong>Note importanti:</strong>
                                                        <ul class="mt-2">
                                                            <li>Solo UPDATE, INSERT e DELETE sono permessi</li>
                                                            <li>Le query UPDATE e DELETE devono includere {id} nella clausola WHERE</li>
                                                            <li>Le query vengono eseguite in transazione (rollback automatico in caso di errore)</li>
                                                        </ul>
                                                    </v-alert>
                                                </div>

                                                <!-- Azione: invia messaggio WhatsApp -->
                                                <div v-else-if="action.type === 'send_whatsapp'">
                                                    <WhatsAppActionForm
                                                        :value="action"
                                                        @input="updateAction(index, $event)"
                                                        :model-type="localAutomation.definition.model_type"
                                                    />
                                                </div>

                                                <!-- Azione: converti lead in prospect -->
                                                <div v-else-if="action.type === 'convert_to_prospect'">
                                                    <v-switch
                                                        v-model="action.transfer_contacts"
                                                        label="Trasferisci Contatti"
                                                        color="primary"
                                                        hide-details
                                                        inset
                                                        class="mb-2"
                                                    ></v-switch>
                                                    <v-switch
                                                        v-model="action.transfer_opportunities"
                                                        label="Trasferisci Opportunità"
                                                        color="primary"
                                                        hide-details
                                                        inset
                                                        class="mb-2"
                                                    ></v-switch>
                                                    <v-switch
                                                        v-model="action.transfer_quotations"
                                                        label="Trasferisci Preventivi"
                                                        color="primary"
                                                        hide-details
                                                        inset
                                                        class="mb-2"
                                                    ></v-switch>
                                                    <v-switch
                                                        v-model="action.create_missing_custom_fields"
                                                        label="Crea campi personalizzati mancanti nel prospect"
                                                        color="primary"
                                                        hide-details
                                                        inset
                                                        class="mb-2"
                                                    ></v-switch>
                                                    <v-switch
                                                        v-model="action.overwrite_existing"
                                                        label="Sovrascrivi valori custom fields esistenti"
                                                        color="primary"
                                                        hide-details
                                                        inset
                                                        class="mb-2"
                                                    ></v-switch>
                                                    <v-switch
                                                        v-model="action.delete_lead"
                                                        label="Elimina Lead dopo la conversione"
                                                        color="primary"
                                                        hide-details
                                                        inset
                                                        class="mb-4"
                                                    ></v-switch>

                                                    <v-alert type="info" dense text>
                                                        Il lead verr&agrave; convertito in un prospect. Verranno copiati tutti i dati
                                                        e, in base alle opzioni selezionate, trasferiti contatti, opportunit&agrave;,
                                                        preventivi e campi personalizzati.
                                                    </v-alert>
                                                </div>
                                            </v-card-text>
                                        </v-card>
                                    </div>

                                    <v-btn color="success" class="mt-2" @click="addAction">
                                        <v-icon left>mdi-plus</v-icon> Aggiungi azione
                                    </v-btn>
                                </v-card-text>
                            </v-card>
                
                            <v-btn color="success" @click="save" :disabled="!isFormValid">
                                <v-icon left>mdi-content-save</v-icon>
                                Salva automazione
                            </v-btn>

                            <v-btn text @click="step = 3" class="ml-2">
                                <v-icon left>mdi-arrow-left</v-icon>
                                Indietro
                            </v-btn>
                        </v-stepper-content>
                    </v-stepper>
                </v-card-text>
            </v-card>
        </v-col>
    </v-row>
</template>

<script>

import InputMixin from '@/mixins/InputMixin'
import CodeEditor from '@/components/common/CodeEditor'
import DynamicField from '@/components/modules/settings/automation/DynamicField'
import WhatsAppActionForm from '@/components/modules/settings/automation/WhatsAppActionForm'

export default {
    name: 'automation-wizard',
    mixins: [InputMixin],
    components: {
        CodeEditor,
        DynamicField,
        WhatsAppActionForm
    },
    props: {
        id: {
            type: Number,
            default: null
        }
    },
    data() {
        return {
			loading: true,
			isEditMode: false,
			automationType: 'entity', // 'entity' o 'report'
			localAutomation: {
				name: '',
				description: '',
				barred: false,
				priority: 1,
				definition: {
					model_type: '',
					execution_type: 'event',
					execution_delay: 0,
					triggers: [],
					conditions: {
						logic: 'and',
						items: []
					},
					actions: [],
					schedule: {
						time: '08:00',
                        cron: '0 0 * * *',  // Default: ogni giorno a mezzanotte
                        date_field: '',      // Campo data per la pianificazione basata su campo
					}
				}
			},
			step: 1,
			timeMenu: false,
			conditionMode: 'simple',
			advancedQuery: '',
			availableModels: [],
			triggerTypes: [
				{ text: 'Evento standard', value: 'event' },
				{ text: 'Modifica di un campo', value: 'field_change' }
			],
			operators: [
				{ text: 'Uguale a (==)', value: '==' },
				{ text: 'Diverso da (!=)', value: '!=' },
				{ text: 'Maggiore di (>)', value: '>' },
				{ text: 'Minore di (<)', value: '<' },
				{ text: 'Minore o uguale a (<=)', value: '<=' },
				{ text: 'Contiene', value: 'contains' },
				{ text: 'Inizia con', value: 'starts_with' },
				{ text: 'Finisce con', value: 'ends_with' },
				{ text: 'In lista', value: 'in' }
			],
			actionTypes: [
				{ text: 'Aggiorna campi', value: 'update_fields' },
				{ text: 'Invia email', value: 'send_email' },
				{ text: 'Invia messaggio WhatsApp', value: 'send_whatsapp' },
				{ text: 'Aggiungi alla Coda di Chiamata', value: 'add_to_call_queue' },
				{ text: 'Esegui Query SQL', value: 'execute_sql' },
				{ text: 'Converti Lead in Prospect', value: 'convert_to_prospect' }
			],
			emailTemplates: [],
			conditionsLogic: 'and',
			scheduleTime: '08:00',
			modelFields: {},
			loadingModelFields: false,
            fieldTypes: {}, // Memorizza il tipo di ciascun campo
            fieldOptions: {}, // Memorizza le opzioni di ciascun campo
            loadingFieldOptions: {}, // Stato di caricamento per le opzioni di ciascun campo
            fieldData: {}, // Memorizza tutti i dati del campo, incluse le opzioni
            emailTemplates: {}, // Cache dei template email per tipo di modello
            emailSignatures: [], // Firme email disponibili
            loadingEmailTemplates: false, // Flag per indicare il caricamento dei template
            callQueueOptions: [], // Opzioni per la coda di chiamate
            loadingCallQueueOptions: false, // Flag per indicare il caricamento delle opzioni
            emailSuggestions: [], // Suggerimenti per indirizzi email
        }
    },

	computed: {
		createTitleDialog() {
			if(this.id) {
				return 'Modifica Automazione'
			} else {
				return 'Crea Automazione'
			}
		},

		// 'sections' e' gestito solo dal path report del backend: su un'automazione
		// entita' il corpo resterebbe vuoto e l'invio fallirebbe con "Email body is empty"
		emailBodyTypes() {
			const types = [
				{ text: 'Template Predefinito', value: 'template' },
				{ text: 'Query SQL', value: 'sql_query' },
				{ text: 'Query SQL in CSV', value: 'sql_query_csv' },
				{ text: 'Testo Personalizzato', value: 'custom' }
			]

			if (this.automationType === 'report') {
				types.push({ text: 'Più query SQL (una tabella per query)', value: 'sections' })
			}

			return types
		},

		filteredActionTypes() {
			// Per automazioni report, limita i tipi di azione disponibili
			if (this.automationType === 'report') {
				return this.actionTypes.filter(type =>
					type.value === 'send_email' || type.value === 'execute_sql' || type.value === 'send_whatsapp'
				)
			}

			const modelType = this.localAutomation.definition.model_type

			// Filtra convert_to_prospect: disponibile solo per lead
			let types = this.actionTypes.filter(type => {
				if (type.value === 'convert_to_prospect' && modelType !== 'lead') {
					return false
				}
				return true
			})

			// Per automazioni su entità, applica la logica per eventi di aggiornamento
			if (this.isUpdateEvent) {
				types = types.map(type => ({
					...type,
					disabled: type.value === 'update_fields',
					hint: type.value === 'update_fields' ? 'Non disponibile per eventi di aggiornamento' : undefined
				}))
			}
			return types
		},

		isStep1Valid() {
			// Per automazioni report, model_type non è richiesto
			if (this.automationType === 'report') {
				return !!this.localAutomation.name
			}
			// Per automazioni su entità, model_type è obbligatorio
			return !!this.localAutomation.name && !!this.localAutomation.definition.model_type
		},

		isStep2Valid() {
			// Logica di validazione aggiornata per lo step 2
			if (this.localAutomation.definition.execution_type === 'event') {
				// Per la modalità basata su eventi, verifica che ci sia almeno un trigger (solo per entità)
				return this.automationType === 'entity' && this.localAutomation.definition.triggers.length > 0
			} else if (this.localAutomation.definition.execution_type === 'schedule') {
				// Per la modalità pianificata con CRON, verifica solo espressione CRON
				return !!this.localAutomation.definition.schedule.cron
			} else if (this.localAutomation.definition.execution_type === 'manual') {
				// Per la modalità manuale, non serve configurazione aggiuntiva
				return true
			} else if (this.localAutomation.definition.execution_type === 'date_field') {
				// Per la modalità basata su campo data, verifica che sia selezionato un campo e un orario (solo per entità)
				return this.automationType === 'entity' &&
					   !!this.localAutomation.definition.schedule.date_field &&
					   !!this.scheduleTime
			}
			return false
		},

		isStep3Valid() {
			// Per automazioni report, le condizioni sono sempre opzionali
			if (this.automationType === 'report') {
				return true
			}
			// Per automazioni su entità con query avanzata
			if (this.conditionMode === 'advanced') {
				return !!this.advancedQuery.trim()
			}
			// Le condizioni non sono obbligatorie in modalità semplice
			return true
    	},

		isStep4Valid() {
			return this.localAutomation.definition.actions.length > 0 &&
				this.localAutomation.definition.actions.every(action => {
				if (action.type === 'update_fields') {
					return action.fields && Object.keys(action.fields).length > 0
				} else if (action.type === 'send_email') {
					// Validazione base
					if (!action.subject) return false

					// Validazione destinatario
					if (action.recipient_type === 'entity_email' && !action.to_field) return false
					if (action.recipient_type === 'custom_email' && !action.to) return false

					// Validazione corpo
					if (action.body_type === 'template' && !action.template_id) return false
					if (action.body_type === 'sql_query' && !action.sql_query) return false
					if (action.body_type === 'sql_query_csv' && !action.sql_query) return false
					if (action.body_type === 'custom' && !action.body) return false
					if (action.body_type === 'sections') {
						if (!action.sections || action.sections.length === 0) return false
						if (action.sections.some(s => !s.sql_query || !s.sql_query.trim())) return false
					}

					return true
				} else if (action.type === 'add_to_call_queue') {
                    return !!action.callqueue_id
                } else if (action.type === 'execute_sql') {
                    return !!action.sql_query && action.sql_query.trim().length > 0
                } else if (action.type === 'send_whatsapp') {
                    // Validazione WhatsApp
                    if (!action.template_id) return false
                    if (action.send_to_contacts && (!action.contact_type || action.contact_type.length === 0)) return false
                    return true
                } else if (action.type === 'convert_to_prospect') {
                    return true
                }
				return false
			})
		},

		isFormValid() {
            return this.isStep1Valid && this.isStep2Valid && this.isStep3Valid && this.isStep4Valid
		},

        // Verifica se è un evento di aggiornamento che non consente "update_fields"
        isUpdateEvent() {
            //return false
            return this.localAutomation.definition.execution_type === 'event' && 
                   this.localAutomation.definition.triggers.some(trigger => 
                       trigger.type === 'event' && trigger.event === 'updated');
        }
  	},

	watch: {
		automationType(newValue) {
			// Quando cambia il tipo di automazione, reimposta i valori correlati
			// Ma solo se non siamo in modalità edit (per preservare i valori caricati)
			if (newValue === 'report') {
				// Per report, azzera model_type
				this.localAutomation.definition.model_type = null
				// Rimuovi i trigger poiché i report non li usano
				this.localAutomation.definition.triggers = []
				// Imposta conditionMode su advanced per report
				this.conditionMode = 'advanced'

				// Solo in modalità creazione, imposta execution_type su schedule di default
				// In modalità edit, mantieni il valore caricato (potrebbe essere 'manual')
				if (!this.isEditMode && this.localAutomation.definition.execution_type === 'event') {
					this.localAutomation.definition.execution_type = 'schedule'
				}
			} else {
				// Per entità, reimposta execution_type a event se era schedule o manual
				if (!this.isEditMode && (this.localAutomation.definition.execution_type === 'schedule' || this.localAutomation.definition.execution_type === 'manual')) {
					this.localAutomation.definition.execution_type = 'event'
				}
				// Reimposta conditionMode su simple per entità
				this.conditionMode = 'simple'
			}
		},

		conditionsLogic(newValue) {
			this.updateConditionsLogic(newValue)
		},

		scheduleTime(newValue) {
			this.saveScheduleTime(newValue)
		},

		'localAutomation.definition.model_type': {
			handler: function(newValue) {
				if (newValue) {
					this.loadModelFields(newValue)
                    this.loadEmailTemplatesForModelType()
				}
			}
		},
		'localAutomation.definition.actions': {
            deep: true,
            handler: function(actions) {
                if (Array.isArray(actions)) {
                    const hasCallQueueAction = actions.some(action => action.type === 'add_to_call_queue')
                    if (hasCallQueueAction && this.callQueueOptions.length === 0) {
                        this.loadCallQueueOptions()
                    }
                }
            }
        }
	},

	async created() {
		try {
			// Determina se siamo in modalità edit o create
			this.isEditMode = !!this.id
			            
			// Se siamo in modalità edit, carica i dati dell'automazione
			if (this.isEditMode) {
                await this.loadAutomationData()
			} else {
                await this.loadConfigurationData()
            }
			
            this.$store.commit('endProgress')

			// Inizializza la struttura dati se necessario
			this.initializeData()
			
			// Se c'è una query SQL avanzata, caricala e imposta la modalità
			if (this.localAutomation.definition.conditions.sql) {
				this.advancedQuery = this.localAutomation.definition.conditions.sql
				this.conditionMode = 'advanced'
			}
			// Supporto retrocompatibilità: vecchio formato con sql in items[0]
			else if (this.localAutomation.definition.conditions.items &&
				this.localAutomation.definition.conditions.items.length &&
				this.localAutomation.definition.conditions.items[0].sql) {
				this.advancedQuery = this.localAutomation.definition.conditions.items[0].sql
				this.conditionMode = 'advanced'
			}
			
			// Imposta il valore iniziale della conditionsLogic
			this.conditionsLogic = this.getConditionsLogic()
			this.scheduleTime = this.getScheduleTime()
			
			this.loading = false
		} catch (error) {
            this.$store.commit('setSnackbar', {
                text: 'Errore durante il caricamento dei dati',
                color: 'error',
                value: true,
            })

			this.loading = false
		}
	},

	methods: {
		async loadAutomationData() {
            try {
                const response = await this.axios.get(`/automations/${this.id}/edit`)

                if (response.data) {
                    // Carica i dati dell'automazione dal campo "model" della risposta
                    this.localAutomation = response.data.model

                    // Imposta il tipo di automazione in base alla presenza di model_type
                    if (!this.localAutomation.definition.model_type) {
                        this.automationType = 'report'
                    } else {
                        this.automationType = 'entity'
                    }

                    // Carica i modelli disponibili
                    this.availableModels = response.data.available_models
                    
                    // Carica i campi del modello direttamente
                    if (response.data.model_fields) {
                        // Memorizza i dati completi di ciascun campo per uso successivo
                        response.data.model_fields.forEach(field => {
                            if (field.key) {
                                this.fieldData[field.key] = field
                                this.fieldTypes[field.key] = field.type || 'text'
                            }
                        })
                        
                        // Aggiorna i campi del modello per la select
                        this.modelFields = {
                            [this.localAutomation.definition.model_type]: response.data.model_fields.map(field => ({
                                text: field.label,
                                value: field.key
                            }))
                        }
                    }

                    // Imposta il flag di caricamento dei campi del modello a false
                    this.loadingModelFields = false
                }
            } catch (error) {
                this.$store.commit('setSnackbar', {
                    text: 'Errore durante il caricamento dell\'automazione:', error,
                    color: 'error',
                    value: true,
                })                
            }
        },
		
		async loadConfigurationData() {
			try {
				// Carica i dati di configurazione necessari
				const response = await this.axios.get('/automations/create')
				if (response.data) {
                    // Imposta i modelli disponibili
                    this.availableModels = response.data.available_models
				}
			} catch (error) {
                this.$store.commit('setSnackbar', {
                    text: 'Errore durante il caricamento dei dati di configurazione:', error,
                    color: 'error',
                    value: true,
                })
			}
		},
		
		async loadModelFields(modelType) {
            if (!modelType) return
            
            // Se stiamo in modalità edit e i campi sono già stati caricati, non fare nulla
            if (this.isEditMode && this.modelFields[modelType] && this.modelFields[modelType].length > 0) {
                return
            }
            
            try {
                this.loadingModelFields = true
                
                // Chiamata API per ottenere i campi specifici per il tipo di modello
                const response = await this.axios.get(`/model-fields/${modelType}`)
                
                if (response.data && response.data.fields) {
                    const fields = response.data.fields
                    
                    // Memorizza i dati completi di ciascun campo per uso successivo
                    fields.forEach(field => {
                        if (field.key) {
                            this.fieldData[field.key] = field
                            this.fieldTypes[field.key] = field.type || 'text'
                        }
                    })
                    
                    // Aggiorna i campi del modello per la select
                    this.modelFields = {
                        [modelType]: fields.map(field => ({
                            text: field.label,
                            value: field.key
                        }))
                    }
                }
                
                this.loadingModelFields = false
            } catch (error) {
                this.$store.commit('setSnackbar', {
                    text: `Errore durante il caricamento dei campi. ${error.message}`,
                    color: 'error',
                    value: true,
                })
                this.loadingModelFields = false
            }
        },
		
		initializeData() {
			// Inizializza la definizione se mancante
			if (!this.localAutomation.definition) {
				this.localAutomation.definition = {}
			}

			// Inizializza i triggers se mancanti
			if (!this.localAutomation.definition.triggers) {
				this.localAutomation.definition.triggers = []
			}
			

			// Inizializza le condizioni se mancanti
			if (!this.localAutomation.definition.conditions) {
				this.localAutomation.definition.conditions = {
					logic: 'and',
					items: []
				}
			}

			// Inizializza le azioni se mancanti
			if (!this.localAutomation.definition.actions) {
				this.localAutomation.definition.actions = []
			}

			// Inizializza il tipo di esecuzione se mancante
			if (!this.localAutomation.definition.execution_type) {
				this.localAutomation.definition.execution_type = 'event'
			}

			// Inizializza il ritardo di esecuzione se mancante (retrocompatibilità
			// con definition esistenti: $set per la reattività di Vue 2)
			if (this.localAutomation.definition.execution_delay === undefined) {
				this.$set(this.localAutomation.definition, 'execution_delay', 0)
			}

			// Inizializza la pianificazione se mancante
			if (!this.localAutomation.definition.schedule) {
				this.localAutomation.definition.schedule = {
					time: '08:00',
                    cron: '0 0 * * *',
                    date_field: ''
				}
			}
			
			// Se mancano specifici campi della pianificazione, inizializzali
            if (!this.localAutomation.definition.schedule.cron) {
                this.localAutomation.definition.schedule.cron = '0 0 * * *'
            }
            if (!this.localAutomation.definition.schedule.date_field) {
                this.localAutomation.definition.schedule.date_field = ''
            }
            if (!this.localAutomation.definition.schedule.time) {
                this.localAutomation.definition.schedule.time = '08:00'
            }
            
            // Imposta il scheduleTime dal valore esistente
            if (this.localAutomation.definition.schedule.time) {
                this.scheduleTime = this.localAutomation.definition.schedule.time
            }
            
            // Controlla se ci sono azioni di tipo add_to_call_queue e carica le opzioni se necessario
            if (this.localAutomation.definition && this.localAutomation.definition.actions) {
                const hasCallQueueAction = this.localAutomation.definition.actions.some(action => action.type === 'add_to_call_queue')
                if (hasCallQueueAction) {
                    this.loadCallQueueOptions()
                }
            }
		},
		
		getModelFields() {
			return this.modelFields[this.localAutomation.definition.model_type] || []
		},
		
		getConditionItems() {
			return this.localAutomation.definition.conditions?.items || []
		},
		
		getConditionsLogic() {
			return this.localAutomation.definition.conditions?.logic || 'and'
		},
		
		updateConditionsLogic(value) {
			if (!this.localAutomation.definition.conditions) {
				this.localAutomation.definition.conditions = {}
			}
			this.localAutomation.definition.conditions.logic = value
		},
		
		getScheduleTime() {
			return this.localAutomation.definition.schedule?.time || '08:00'
		},
		
		saveScheduleTime(value) {
			if (!this.localAutomation.definition.schedule) {
				this.localAutomation.definition.schedule = {}
			}
			this.localAutomation.definition.schedule.time = value
		},
		
		nextStep(step) {
			this.step = step + 1
		},
		
		addTrigger() {
            this.localAutomation.definition.triggers.push({
                type: 'event',
                event: 'created',
                field: '',
                from: '',
                to: '',
                condition: 'any'
            })
        },
		
		removeTrigger(index) {
			this.localAutomation.definition.triggers.splice(index, 1)
		},
		
		addCondition() {
			if (!this.localAutomation.definition.conditions.items) {
				this.localAutomation.definition.conditions.items = []
			}
				
			const index = this.localAutomation.definition.conditions.items.length
				
			this.localAutomation.definition.conditions.items.push({
                field: '',
                operator: '==',
                value: ''
			})
				
			// Inizializza lo stato di caricamento per questa condizione
			this.loadingFieldOptions[index] = false
		},
		
		removeCondition(index) {
			this.localAutomation.definition.conditions.items.splice(index, 1)
		},
		
		addAction() {
            this.localAutomation.definition.actions.push({
                type: '',
                fields: [],
                // Email fields
                body_type: 'template', // Default to template
                template_id: '',
                sql_query: '',
                sections: [],
                body: '',
                subject: '',
                recipient_type: 'entity_email',
                to_field: '',
                to: '',
                from: '',
                from_name: '',
                cc: [],
                ccn: [],
                // Other action fields
                callqueue_id: '',
                sql_query_execute: '',
                // WhatsApp fields
                send_to_contacts: false,
                contact_type: [],
                send_to_company_telephone: false,
                send_to_company_telephone_always: false,
                // Convert to prospect fields
                transfer_contacts: false,
                transfer_opportunities: false,
                transfer_quotations: false,
                create_missing_custom_fields: false,
                overwrite_existing: true,
                delete_lead: true
            })
        },
		
		removeAction(index) {
			this.localAutomation.definition.actions.splice(index, 1)
		},

		// Passando a piu' query, la prima eredita la query gia' scritta in "Query SQL":
		// senza questo l'utente la perderebbe e dovrebbe reincollarla a mano
		onBodyTypeChange(action, value) {
			if (value !== 'sections') return

			if (!Array.isArray(action.sections)) {
				this.$set(action, 'sections', [])
			}

			if (action.sections.length === 0) {
				action.sections.push({
					title: '',
					description: '',
					sql_query: action.sql_query || ''
				})
			}
		},

		// $set: le automazioni salvate prima delle sezioni non hanno la chiave, Vue 2 non la renderebbe reattiva
		addSection(action) {
			if (!action.sections) {
				this.$set(action, 'sections', [])
			}
			action.sections.push({ title: '', description: '', sql_query: '' })
		},

		removeSection(action, index) {
			action.sections.splice(index, 1)
		},

		moveSection(action, index, delta) {
			const target = index + delta
			if (target < 0 || target >= action.sections.length) return

			const sections = action.sections.slice()
			const [moved] = sections.splice(index, 1)
			sections.splice(target, 0, moved)
			this.$set(action, 'sections', sections)
		},

		updateAction(index, updatedAction) {
			this.$set(this.localAutomation.definition.actions, index, updatedAction)
		},
		
		addUpdateField(action) {
            if (!action.fields) {
                action.fields = []
            }
            
            // Aggiungi il nuovo campo alla lista
            action.fields.push({
                key: '',
                value: '',
                isDynamic: false
            })
            
            // Forza il re-rendering del componente
            this.$forceUpdate()
        },
        
        removeUpdateField(action, index) {
            // Rimuove il campo all'indice specificato
            action.fields.splice(index, 1)
            
            // Forza il re-rendering del componente
            this.$forceUpdate()
        },
		
		async save() {
            try {
                this.loading = true
                let response
                
                // Gestisci condizioni: SQL avanzata OPPURE condizioni semplici (mai entrambe)
                if (this.conditionMode === 'advanced') {
                    // Modalità SQL avanzata: rimuovi items, tieni solo sql
                    this.localAutomation.definition.conditions = {
                        sql: this.advancedQuery
                    }
                } else {
                    // Modalità semplice: rimuovi sql, tieni solo items
                    if (this.localAutomation.definition.conditions.sql) {
                        delete this.localAutomation.definition.conditions.sql
                    }
                }
                
                // Crea una deep copy dei dati da inviare
                const dataToSend = JSON.parse(JSON.stringify(this.localAutomation))

                // Fix: preserva variables_mapping come oggetto
                // JSON.stringify converte oggetti con chiavi numeriche in array
                if (this.localAutomation.definition?.actions) {
                    this.localAutomation.definition.actions.forEach((action, index) => {
                        if (action.variables_mapping && typeof action.variables_mapping === 'object' && !Array.isArray(action.variables_mapping)) {
                            // Ricrea l'oggetto con chiavi stringa per preservare il formato
                            dataToSend.definition.actions[index].variables_mapping = { ...action.variables_mapping }
                        }
                    })
                }

                // Per automazioni report, assicurati che model_type sia null
                if (this.automationType === 'report') {
                    dataToSend.definition.model_type = null
                    // I report non hanno trigger basati su eventi
                    if (dataToSend.definition.execution_type === 'event') {
                        dataToSend.definition.execution_type = 'schedule'
                    }
                }

                // Normalizza il ritardo di esecuzione (minuti, intero >= 0) per ogni
                // tipo di esecuzione: con v-model.number il campo svuotato diventa ''
                dataToSend.definition.execution_delay = Math.max(0, parseInt(dataToSend.definition.execution_delay, 10) || 0)

                // Pulisce i campi non necessari in base al tipo di esecuzione
                if (dataToSend.definition.execution_type === 'event') {
                    // Se è basato su eventi, svuota i campi di pianificazione
                    dataToSend.definition.schedule.date_field = null
                    dataToSend.definition.schedule.cron = '0 0 * * *' // valore default
                } else if (dataToSend.definition.execution_type === 'schedule') {
                    // Se è pianificato con CRON, svuota il campo date_field
                    dataToSend.definition.schedule.date_field = null
                } else if (dataToSend.definition.execution_type === 'manual') {
                    // Se è manuale, svuota i campi di pianificazione
                    dataToSend.definition.schedule.date_field = null
                    dataToSend.definition.schedule.cron = null
                } else if (dataToSend.definition.execution_type === 'date_field') {
                    // Se è basato su campo data, svuota il campo CRON
                    dataToSend.definition.schedule.cron = '0 0 * * *' // valore default
                }
                
                // Salva i dati in base alla modalità
                if (this.isEditMode) {
                    response = await this.axios.put(`/automations/${this.id}`, dataToSend)
                } else {
                    response = await this.axios.post('/automations', dataToSend)
                }
                
                this.loading = false
                
                if (response.data) {
                    this.$router.push('/settingsgeneral/automations')
                }
            } catch (error) {
                this.$store.commit('setSnackbar', {
                    text: `Errore durante il salvataggio: ${error.response?.data?.message || error.message}`,
                    color: 'error',
                    value: true,
                })
                this.loading = false
            }
        },
        
        onFieldChange(condition, index) {
            // Reset del valore
            condition.value = ''
        },
        
        onOperatorChange(condition, index) {
            // Reset del valore se è cambiato il tipo di input necessario
            if (condition.operator === 'in') {
                condition.value = []
            } else if (Array.isArray(condition.value)) {
                condition.value = ''
            }
        },
        
        getFieldType(fieldName) {
            return this.fieldTypes[fieldName] || 'text'
        },
        
        getFieldOptions(fieldName) {
            if (!fieldName || !this.fieldData[fieldName]) {
                return []
            }
            
            const field = this.fieldData[fieldName]
            
            // Verifica se il campo ha opzioni direttamente definite
            if (field.options) {
                return field.options
            }
            
            return []
        },
        
        getFieldTypeForInput(fieldName) {
            if (!fieldName) return 'text'
            
            const fieldType = this.getFieldType(fieldName)
            return fieldType
        },

        getDateFields() {
            return this.getModelFields().filter(field => {
                const fieldType = this.getFieldType(field.value)
                return fieldType === 'date' || fieldType === 'datetime'
            })
        },

        onTriggerFieldChange(trigger, index) {
            // Reset dei valori
            trigger.from = ''
            trigger.to = ''
        },
        
        onActionTypeChange(action, index) {
            if (action.type === 'send_email') {
                this.loadEmailTemplatesForModelType()
            } else if (action.type === 'add_to_call_queue') {
                this.loadCallQueueOptions()
            }
        },
        

        loadEmailTemplatesForModelType() {
            const modelType = this.localAutomation.definition.model_type
            if (!modelType) return // Nessun model_type specificato

            this.loadingEmailTemplates = true

            // Carica i template email
            this.axios.post('getoptionfields/', {
                model: 'Template',
                relation_field: 'name',
                key: 'text',
                value: 'value',
                filters: {
                    'type': { condition: 'where', operator: '=', value: modelType.toLowerCase() }
                }
            }).then(response => {
                if (response.data && response.data.option_fields) {
                    // Mappa i template nel formato corretto per v-select
                    this.emailTemplates[modelType] = response.data.option_fields.map(item => ({
                        text: item.text,
                        value: item.value
                    }))
                }
            }).catch(error => {
                console.error('Errore nel caricamento dei template:', error)
            })

            // Carica i suggerimenti email (utenti del sistema)
            this.axios.post('getoptionfields/', {
                model: 'User',
                relation_field: 'email',
                key: 'text',
                value: 'value'
            }).then(response => {
                if (response.data && response.data.option_fields) {
                    this.emailSuggestions = response.data.option_fields.map(item => item.text)
                }
            }).catch(error => {
                console.error('Errore nel caricamento degli utenti:', error)
            }).finally(() => {
                this.loadingEmailTemplates = false
            })
        },
        
        loadCallQueueOptions() {
            this.loadingCallQueueOptions = true
            
            // Chiamata API per caricare le opzioni della coda chiamate
            this.axios.post('getoptionfields/', {
                model: 'Callqueue', 
                relation_field: 'name', 
                key: 'text', 
                value: 'value'
            }).then(response => {
                if (response.data && response.data.option_fields) {
                    this.callQueueOptions = response.data.option_fields
                }
            }).catch(error => {
                this.$store.commit('setSnackbar', {
                    text: `Errore durante il caricamento delle opzioni: ${error.message}`,
                    color: 'error',
                    value: true,
                })
            }).finally(() => {
                this.loadingCallQueueOptions = false
            })
        },

        // Metodo per filtrare i tipi di azione disponibili basati sul contesto
        getAvailableActionTypes(action) {
            if (this.isUpdateEvent) {
                // Filtra l'opzione "aggiorna campi" se è un evento di aggiornamento
                return this.actionTypes.map(type => ({
                    ...type,
                    disabled: type.value === 'update_fields',
                    hint: type.value === 'update_fields' ? 'Non disponibile per eventi di aggiornamento' : undefined
                }));
            }
            return this.actionTypes;
        },
    }
}
</script>